import React from "react";
import LegalLayout from "../components/LegalLayout";

const P = ({ children }) => (
  <p className="mb-4 text-slate-700">{children}</p>
);
const UL = ({ children }) => (
  <ul className="mb-4 list-disc space-y-1.5 pl-6 text-slate-700">{children}</ul>
);
const H3 = ({ children }) => (
  <h3 className="mb-2 mt-5 text-base font-semibold text-[#1e1b4b]">{children}</h3>
);

const sections = [
  {
    id: "overview",
    heading: "Our Commitment",
    content: (
      <P>
        BuyerBeacon Pro is built on a foundation of regulatory rigor. Marketing
        data is only valuable when it's responsibly sourced, lawfully
        processed, and ethically activated. Below is a summary of the
        frameworks we adhere to and the practices we follow.
      </P>
    ),
  },
  {
    id: "frameworks",
    heading: "Regulatory Frameworks",
    content: (
      <>
        <H3>CCPA / CPRA (California)</H3>
        <P>
          We honor California consumers' rights to know, delete, correct,
          limit, and opt out of "sale" or "sharing" of personal information.
          Requests can be submitted at{" "}
          <a
            href="https://buyerbeacon.pro/optout"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            buyerbeacon.pro/optout
          </a>{" "}
          or by emailing{" "}
          <a
            href="mailto:admin@buyerbeacon.pro"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            admin@buyerbeacon.pro
          </a>
          . We respond within forty-five (45) days.
        </P>

        <H3>GDPR (European Union)</H3>
        <P>
          For European data subjects (where applicable), we process data on a
          lawful basis (legitimate interest, contract, or consent). We honor
          access, rectification, erasure, restriction, portability, and
          objection requests.
        </P>

        <H3>CAN-SPAM Act (U.S.)</H3>
        <P>
          All marketing communications enabled by our Services must include a
          functional unsubscribe mechanism, accurate sender identification, a
          physical postal address, and must not use deceptive subject lines or
          headers.
        </P>

        <H3>TCPA (U.S.)</H3>
        <P>
          Customers using our platform for SMS or voice channels are solely
          responsible for obtaining and maintaining the prior express written
          consent required under the TCPA and equivalent state laws (Florida
          FTSA, Washington CEMA, Oklahoma TCPA, etc.).
        </P>

        <H3>Other U.S. State Privacy Laws</H3>
        <P>
          We track and adapt to evolving state regimes including the Virginia
          CDPA, Colorado CPA, Connecticut CTDPA, Utah UCPA, Texas TDPSA, and
          similar Oregon and Montana laws as they take effect.
        </P>
      </>
    ),
  },
  {
    id: "self-regulatory",
    heading: "Self-Regulatory Standards",
    content: (
      <UL>
        <li>
          <strong>IAB</strong> — Interactive Advertising Bureau guidelines
          (including the Global Privacy Platform / GPP for consent signaling)
        </li>
        <li>
          <strong>DAA</strong> — Digital Advertising Alliance Self-Regulatory
          Principles
        </li>
        <li>
          <strong>NAI</strong> — Network Advertising Initiative Code of Conduct
        </li>
        <li>
          <strong>EDAA</strong> — European Interactive Digital Advertising
          Alliance (where applicable)
        </li>
      </UL>
    ),
  },
  {
    id: "permissible-use",
    heading: "Permissible-Use Restrictions",
    content: (
      <>
        <P>Available Data may NOT be used in connection with:</P>
        <UL>
          <li>Credit eligibility, credit scoring, or credit repair</li>
          <li>Insurance underwriting</li>
          <li>Employment screening</li>
          <li>Tenant screening</li>
          <li>
            Pornography, gambling solicitation to minors, illegal drugs,
            illegal weapons, or any other unlawful purpose
          </li>
          <li>
            Targeting based on legally protected classes in a discriminatory
            manner
          </li>
          <li>
            Inferring or revealing sensitive attributes (health, sexual
            orientation, religion, political belief) in marketing creative
          </li>
        </UL>
      </>
    ),
  },
  {
    id: "consumer-rights",
    heading: "Consumer Rights & Opt-Out",
    content: (
      <UL>
        <li>
          <strong>Universal Opt-Out:</strong>{" "}
          <a
            href="https://buyerbeacon.pro/optout"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            buyerbeacon.pro/optout
          </a>
        </li>
        <li>
          <strong>Email Removal:</strong> Use the unsubscribe link in any
          email; allow up to 10 business days for propagation.
        </li>
        <li>
          <strong>Direct Mail:</strong> Use the DMA's Choice service at
          thedma.org.
        </li>
        <li>
          <strong>Cookie-Based Retargeting:</strong> Visit your browser's
          cookie settings or use the universal opt-out URL above (per-browser).
        </li>
        <li>
          <strong>Sensitive Information Limits (CA):</strong> Email{" "}
          <a
            href="mailto:admin@buyerbeacon.pro"
            className="text-[#1e1b4b] underline-offset-4 hover:underline"
          >
            admin@buyerbeacon.pro
          </a>
          .
        </li>
      </UL>
    ),
  },
  {
    id: "responsibilities",
    heading: "Customer & Affiliate Responsibilities",
    content: (
      <>
        <P>
          If you are a customer or affiliate of BuyerBeacon Pro, you agree to:
        </P>
        <UL>
          <li>
            Maintain a public-facing privacy policy that discloses your use of
            third-party data providers, including BuyerBeacon Pro where
            applicable
          </li>
          <li>
            Honor consumer opt-outs received through your channels and
            propagate them to BuyerBeacon Pro within 10 business days
          </li>
          <li>
            Use Available Data only for the Authorized Applications set forth
            in your Service Order
          </li>
          <li>
            Maintain documented consent for SMS, voice, or other regulated
            channels
          </li>
          <li>
            Provide BuyerBeacon Pro with prompt notice of any regulatory
            inquiry or breach
          </li>
        </UL>
      </>
    ),
  },
  {
    id: "report",
    heading: "Reporting a Compliance Concern",
    content: (
      <P>
        We take all reports of misuse seriously. Email{" "}
        <a
          href="mailto:admin@buyerbeacon.pro"
          className="text-[#1e1b4b] underline-offset-4 hover:underline"
        >
          admin@buyerbeacon.pro
        </a>{" "}
        with the subject line "Compliance Report" and include as much detail
        as you can. We investigate promptly and confidentially.
      </P>
    ),
  },
];

export default function CompliancePage() {
  return (
    <LegalLayout
      eyebrow="Trust"
      title="Compliance"
      lastUpdated="February 2026"
      path="/compliance"
      seoDescription="BuyerBeacon Pro compliance — CCPA/CPRA, GDPR, CAN-SPAM, TCPA, IAB, DAA, NAI standards. Permissible-use restrictions and consumer rights."
      sections={sections}
    />
  );
}
